import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Users, UserCircle, FileText, Calendar, AlertTriangle, DollarSign, Briefcase, BarChart3 } from 'lucide-react';
import { roleLabel } from '@/lib/roleLabels';

const MODULES = [
  { name: 'Participants', path: '/participants/list', icon: Users, keywords: 'participant client ndis' },
  { name: 'Staff Profiles', path: '/staff/list', icon: UserCircle, keywords: 'staff worker employee' },
  { name: 'Scheduling Board', path: '/roster/scheduling-board', icon: Calendar, keywords: 'roster shift schedule board' },
  { name: 'Timesheets', path: '/timesheets/pending', icon: Briefcase, keywords: 'timesheet approve hours' },
  { name: 'Invoices', path: '/finance/invoices', icon: FileText, keywords: 'invoice billing payment' },
  { name: 'Claims', path: '/finance/claims', icon: DollarSign, keywords: 'claim ndis proda export' },
  { name: 'Incidents', path: '/compliance/incidents', icon: AlertTriangle, keywords: 'incident report safety' },
  { name: 'Medications', path: '/clinical/medications', icon: Briefcase, keywords: 'medication emar drug' },
  { name: 'Reports', path: '/reports', icon: BarChart3, keywords: 'report analytics data' },
  { name: 'AI Copilot', path: '/ai', icon: Briefcase, keywords: 'ai copilot question insight' },
  { name: 'Appointments', path: '/engagement/appointments', icon: Calendar, keywords: 'appointment gp therapy' },
  { name: 'Messages', path: '/engagement/messages', icon: Briefcase, keywords: 'message communication' },
  { name: 'Security', path: '/admin/security', icon: Briefcase, keywords: 'security audit permission' },
  { name: 'System Admin', path: '/admin/system', icon: Briefcase, keywords: 'system backup logs admin' },
];

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ participants: [], staff: [], modules: [] });
  const [selected, setSelected] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  // Cmd+K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) { setQuery(''); setResults({ participants: [], staff: [], modules: [] }); setSelected(0); }
  }, [open]);

  const search = useCallback(async (q) => {
    if (!q.trim()) { setResults({ participants: [], staff: [], modules: [] }); return; }
    setLoading(true);
    const ql = q.toLowerCase();

    // Module search (instant)
    const modules = MODULES.filter(m => m.name.toLowerCase().includes(ql) || m.keywords.includes(ql));

    // API search
    let participants = [], staff = [];
    try {
      const [pRes, sRes] = await Promise.allSettled([
        api.get(`/participants?search=${encodeURIComponent(q)}&page=1&pageSize=5`),
        api.get(`/admin/users?search=${encodeURIComponent(q)}&page=1&pageSize=5`),
      ]);
      participants = pRes.status === 'fulfilled' ? (pRes.value.data.items || pRes.value.data.data || []).slice(0, 5) : [];
      staff = sRes.status === 'fulfilled' ? (sRes.value.data.items || sRes.value.data.data || []).slice(0, 5) : [];
    } catch {}

    setResults({ participants, staff, modules: modules.slice(0, 5) });
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!query.trim()) { setResults({ participants: [], staff: [], modules: [] }); return; }
    const timer = setTimeout(() => search(query), 200);
    return () => clearTimeout(timer);
  }, [query, search]);

  const allItems = [
    ...results.modules.map(m => ({ type: 'module', ...m })),
    ...results.participants.map(p => ({ type: 'participant', name: `${p.first_name} ${p.last_name}`, sub: p.ndis_number || '', path: `/participants/${p.id}`, icon: Users })),
    ...results.staff.map(s => ({ type: 'staff', name: s.full_name, sub: roleLabel(s.role_name) || s.email, path: `/staff/${s.id || s.user_id}`, icon: UserCircle })),
  ];

  const go = (path) => { navigate(path); setOpen(false); };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(s + 1, allItems.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)); }
    else if (e.key === 'Enter' && allItems[selected]) { go(allItems[selected].path); }
  };

  return (
    <>
      {/* Trigger button in topbar */}
      <button
        onClick={() => setOpen(true)}
        className="hidden md:flex items-center gap-2 h-9 px-3 rounded-lg border bg-muted/50 text-muted-foreground hover:bg-muted transition-colors text-sm"
        data-testid="global-search-trigger"
      >
        <Search className="h-4 w-4" />
        <span>Search...</span>
        <kbd className="ml-4 text-[10px] bg-background border rounded px-1.5 py-0.5 font-mono">⌘K</kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="p-0 max-w-lg gap-0 overflow-hidden" data-testid="global-search-dialog">
          <div className="flex items-center border-b px-4">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <Input
              ref={inputRef}
              autoFocus
              placeholder="Search participants, staff, modules..."
              value={query}
              onChange={e => { setQuery(e.target.value); setSelected(0); }}
              onKeyDown={handleKeyDown}
              className="border-0 focus-visible:ring-0 h-12 text-base"
              data-testid="global-search-input"
            />
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {!query.trim() && (
              <div className="p-6 text-center text-sm text-muted-foreground">
                <p>Type to search participants, staff, or modules</p>
                <p className="text-xs mt-1">Tip: Use <kbd className="bg-muted px-1 rounded text-[10px]">⌘K</kbd> from anywhere</p>
              </div>
            )}

            {query.trim() && allItems.length === 0 && !loading && (
              <div className="p-6 text-center text-sm text-muted-foreground">No results for "{query}"</div>
            )}

            {results.modules.length > 0 && (
              <div className="px-2 pt-2">
                <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Modules</p>
                {results.modules.map((m, i) => {
                  const idx = i;
                  const Icon = m.icon;
                  return (
                    <button key={m.path} onClick={() => go(m.path)}
                      className={`flex items-center gap-3 w-full px-3 py-2 rounded-md text-sm text-left transition-colors ${selected === idx ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'}`}>
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span>{m.name}</span>
                      <Badge variant="outline" className="ml-auto text-[10px]">Module</Badge>
                    </button>
                  );
                })}
              </div>
            )}

            {results.participants.length > 0 && (
              <div className="px-2 pt-2">
                <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Participants</p>
                {results.participants.map((p, i) => {
                  const idx = results.modules.length + i;
                  return (
                    <button key={p.id} onClick={() => go(`/participants/${p.id}`)}
                      className={`flex items-center gap-3 w-full px-3 py-2 rounded-md text-sm text-left transition-colors ${selected === idx ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'}`}>
                      <Users className="h-4 w-4 text-emerald-500" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{p.first_name} {p.last_name}</p>
                        {p.ndis_number && <p className="text-xs text-muted-foreground">{p.ndis_number}</p>}
                      </div>
                      <Badge variant="outline" className="text-[10px]">Participant</Badge>
                    </button>
                  );
                })}
              </div>
            )}

            {results.staff.length > 0 && (
              <div className="px-2 pt-2 pb-2">
                <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Staff</p>
                {results.staff.map((s, i) => {
                  const idx = results.modules.length + results.participants.length + i;
                  return (
                    <button key={s.id} onClick={() => go(`/admin/users`)}
                      className={`flex items-center gap-3 w-full px-3 py-2 rounded-md text-sm text-left transition-colors ${selected === idx ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'}`}>
                      <UserCircle className="h-4 w-4 text-blue-500" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{s.full_name}</p>
                        <p className="text-xs text-muted-foreground">{roleLabel(s.role_name) || s.email}</p>
                      </div>
                      <Badge variant="outline" className="text-[10px]">Staff</Badge>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
